import SalesOrderLineInterface from '@/interfaces/SalesOrderLineInterface';
import { Plus, Minus } from 'lucide-react';
import Bin from '../../../assets/icons/delete.svg?react';
import Edit from '../../../assets/icons/edit.svg?react';
// import ZoomIn from '../../../assets/icons/zoom-in.svg?react';
import PlaceholderImage from '../../../assets/images/placeholder.svg';
import LinesUtility from '@/utility/lines-utility';
import { ZoomIn } from 'lucide-react';

interface OrdersListProps {
  lines: SalesOrderLineInterface[];
  readOnly: boolean;
  onPersonalise: (line: SalesOrderLineInterface) => void;
  onChangeLine: (line: SalesOrderLineInterface) => void;
  onRemoveLine: (line: SalesOrderLineInterface) => void;
  onUseExistingDesign?: (line: SalesOrderLineInterface) => void;
  onPreviewClick: (imageUrl: string) => void;
  updateLine: (line: SalesOrderLineInterface) => void;
  productsChecked: boolean;
}

const getLineName = (line: SalesOrderLineInterface) =>
  line.Sold_As_Description || line.Description || 'Product awaiting details';

const getLineCode = (line: SalesOrderLineInterface) => line.Sold_as_SKU || line.Item_No_2 || line.No;

const getQuantity = (line: SalesOrderLineInterface) => {
  const quantity = Number.parseFloat(line.Quantity);
  return Number.isFinite(quantity) ? quantity : 0;
};

export default function OrdersList({
  lines = [],
  onPersonalise,
  onChangeLine,
  updateLine,
  onPreviewClick,
  onRemoveLine,
  readOnly,
  productsChecked = false,
}: OrdersListProps) {
  const products = LinesUtility.products(lines);

  // TODO group lines by External_Line_Number
  return (
    <ul aria-label="Products to personalise" className="grid gap-[12px] mt-10">
      {products.map((line) => {
        const lineName = getLineName(line);
        const lineCode = getLineCode(line);
        const totalQuantity = LinesUtility.totalQuantity(lines, line);
        const personalisedQuantity = LinesUtility.personalisedQuantity(lines, line);
        const isPersonalised = totalQuantity > 0 && personalisedQuantity === totalQuantity;

        const personalisedProductLines = lines.filter(
          (productLine) =>
            productLine.External_Document_Line_No === line.External_Document_Line_No &&
            LinesUtility.isPersonalised(productLine),
        );

        const handleExpandButtonClick = (line: SalesOrderLineInterface) => {
          updateLine({ ...line, Expanded: !line.Expanded });
        };

        return (
          <div
            key={line.Line_No}
            className="flex min-w-0 flex-col border-1 border-gray-200 bg-white p-[14px] sm:p-[20px] lg:p-[24px]"
          >
            {/* PRODUCT INFO/PROGRESS */}
            <div className="flex flex-row gap-[12px] sm:gap-[20px]">
              <div>
                <button
                  aria-label={`Preview ${lineName}`}
                  className="h-[100px] w-[100px] shrink-0 overflow-hidden p-6 sm:h-[120px] sm:w-[120px] bg-[#f9f9f9] rounded-3xl relative cursor-pointer"
                  onClick={() => onPreviewClick(line.Product_URL_1)}
                  type="button"
                >
                  <img
                    alt="product-image"
                    className="h-full w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = PlaceholderImage;
                    }}
                    src={line.Product_URL_1 || PlaceholderImage}
                  />
                  {/* border-1 border-gray-300 bg-white rounded-full */}
                  <div className="flex items-center justify-center absolute bottom-1 right-1 w-[30px] h-[30px]">
                    <ZoomIn className="w-full h-full p-2" color="#d3d3d3" />
                  </div>
                </button>
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-[12px] lg:flex-row lg:items-center lg:gap-[24px]">
                <div className="min-w-0 flex-1">
                  <div className="break-words text-[20px] font-bold leading-[1.25] sm:text-[24px] lg:text-[28px]">
                    {lineName}
                  </div>
                  <div className="mt-[6px] break-words text-[14px] leading-[1.5] text-slate-600 lg:text-[16px] font-normal font-[Arial]">
                    <span>{lineCode || `Item ${line.No}`}</span>
                  </div>
                </div>

                <div className="flex min-w-0 flex-col gap-[8px] lg:flex-1 lg:justify-center">
                  <div className="text-[15px] leading-[1.5] text-slate-600 lg:text-[18px] font-normal font-[Arial]">
                    {personalisedQuantity} of {totalQuantity} pieces personalised
                  </div>
                  <div
                    aria-label={`${personalisedQuantity} of ${totalQuantity} pieces personalised`}
                    aria-valuemax={totalQuantity}
                    aria-valuemin={0}
                    aria-valuenow={personalisedQuantity}
                    className="w-full h-[8px] overflow-hidden rounded-full bg-slate-200 lg:h-[10px]"
                    role="progressbar"
                  >
                    <div
                      className="h-full rounded-full bg-green-600 transition-[width] duration-500 ease-out"
                      style={{ width: `${totalQuantity === 0 ? 0 : (personalisedQuantity / totalQuantity) * 100}%` }}
                    />
                  </div>
                </div>

                {readOnly === false && (
                  <div className="hidden shrink-0 items-center justify-end lg:flex">
                    <div className="flex flex-1 flex-col items-end justify-center">
                      <button
                        aria-label={`Personalise ${lineName}`}
                        className={`inline-flex min-h-[48px] w-full items-center justify-center button-screwfix-primary px-[16px] font-bold lg:w-auto lg:min-w-[160px]`}
                        onClick={() => onPersonalise(line)}
                        disabled={isPersonalised || productsChecked === false}
                        type="button"
                      >
                        <span className="text-[22px] leading-[1.3]">Personalise</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* PERSONALISE BUTTON */}
            {isPersonalised === false && readOnly === false && (
              <div className="flex flex-1 flex-col items-end justify-center mt-[16px] lg:hidden">
                <button
                  aria-label={`${isPersonalised ? 'Change design for' : 'Personalise'} ${lineName}`}
                  className={`inline-flex min-h-[44px] w-full items-center justify-center button-screwfix-primary px-[16px] text-[18px] leading-[1.3] font-bold`}
                  onClick={() => onPersonalise(line)}
                  disabled={productsChecked === false}
                  type="button"
                >
                  Personalise
                </button>
              </div>
            )}

            {/* PERSONALISED ITEMS */}
            {personalisedQuantity > 0 && (
              <>
                <div className="my-[18px] border-t border-dashed border-[#e5e5e5]" />

                <div className="flex flex-row gap-[12px] mb-5">
                  <div className="flex flex-1 justify-start items-center">
                    <div className="text-[16px] leading-[1.5] font-normal font-[Arial] text-slate-600 lg:text-[18px]">
                      Personalised items ({personalisedQuantity})
                    </div>
                  </div>
                  <div className="flex justify-end items-center">
                    <button
                      aria-label={`${line.Expanded ? 'Collapse' : 'Expand'} personalised items for ${lineName}`}
                      className="flex shrink-0 items-center justify-center rounded bg-[#d1d1d1] h-[28px] w-[28px] sm:h-[44px] sm:w-[44px] cursor-pointer"
                      onClick={() => handleExpandButtonClick(line)}
                      type="button"
                    >
                      {line?.Expanded ? (
                        <Minus color="white" size={'20px'} className="min-w-[20px]" />
                      ) : (
                        <Plus color="white" size={'20px'} className="min-w-[20px]" />
                      )}
                    </button>
                  </div>
                </div>

                {/* LINES */}
                {line.Expanded && personalisedProductLines.length > 0 && (
                  <div className="mt-5 flex flex-col gap-[10px]">
                    {personalisedProductLines.map((personalisedLine) => (
                      <div
                        key={`${personalisedLine.No}-${personalisedLine.Item_No_2}-${personalisedLine.Line_No}`}
                        className="flex min-w-0 flex-row gap-[12px] border border-[#f0f0f3] bg-white p-[12px] sm:gap-[20px] sm:p-[20px]"
                      >
                        <button
                          aria-label={`Preview ${lineName}`}
                          className={`w-[50%] ${readOnly && 'w-[100%]'} self-start aspect-square shrink-0 sm:w-[160px] lg:w-[180px] bg-[#f9f9f9] rounded-3xl relative cursor-pointer`}
                          onClick={() => onPreviewClick(personalisedLine.Product_URL_3)}
                          type="button"
                        >
                          <img
                            alt="product-image"
                            className="h-full w-full object-contain"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = PlaceholderImage;
                            }}
                            src={personalisedLine.Product_URL_3 || PlaceholderImage}
                          />

                          <div className="absolute -left-5 -top-5 z-1 flex min-h-[34px] min-w-[34px] items-center justify-center rounded-full bg-[#0053a0] px-[6px] font-[Arial] text-[16px] font-bold leading-[1.3] text-white border-2 border-white shadow-sm lg:min-h-[32px] lg:min-w-[32px] lg:text-[16px]">
                            {getQuantity(personalisedLine)}
                          </div>
                          <div className="flex items-center justify-center absolute bottom-3 right-3 w-[30px] h-[30px]">
                            <ZoomIn className="w-full h-full p-2" color="#d3d3d3" />
                          </div>
                        </button>

                        {/* MOBILE ACTION BUTTONS */}
                        {readOnly === false && (
                          <div className="sm:hidden flex min-w-0 flex-1 flex-row justify-center gap-[8px] sm:flex-row sm:items-center sm:justify-end sm:gap-[12px]">
                            <button
                              aria-label={`Change personalisation for ${lineName}`}
                              className="flex min-h-[44px] min-w-0 flex-1 sm:flex-none flex-col gap-[8px] items-center justify-center text-screwfix-blue-shade-2 px-[4px] text-[16px] leading-[1.4] sm:flex-row lg:text-[20px] cursor-pointer sm:button-screwfix-secondary"
                              onClick={() => onChangeLine(personalisedLine)}
                              type="button"
                            >
                              <span>
                                <Edit className="fill-screwfix-blue-shade-2 h-[22px] w-[22px] lg:h-[26px] lg:w-[26px]" />
                              </span>
                              Change
                            </button>
                            <div className="self-center h-[60%] border-r border-[#ebebeb] sm:hidden"></div>
                            <button
                              aria-label={`Remove personalisation from ${lineName}`}
                              className="flex min-h-[44px] min-w-0 flex-1 sm:flex-none flex-col gap-[8px] items-center justify-center text-screwfix-red-shade-1 px-[4px] text-[16px] leading-[1.4] lg:text-[20px] cursor-pointer sm:flex-row lg:button-screwfix-danger"
                              onClick={() => onRemoveLine(personalisedLine)}
                              type="button"
                            >
                              <span>
                                <Bin className="fill-screwfix-red-shade-1 h-[22px] w-[22px] lg:h-[26px] lg:w-[26px]" />
                              </span>
                              Remove
                            </button>
                          </div>
                        )}

                        {/* DESKTOP ACTION BUTTONS */}
                        {readOnly === false && (
                          <div className="hidden sm:flex min-w-0 flex-1 flex-row justify-center gap-[8px] sm:flex-row sm:items-center sm:justify-end sm:gap-[12px]">
                            <button
                              aria-label={`Change personalisation for ${lineName}`}
                              className="flex min-h-[44px] flex-row gap-[14px] items-center justify-center rounded-[5px] bg-transparent px-[30px] py-[16px] text-[16px] font-normal leading-[1.4] text-screwfix-blue-shade-2 lg:text-[20px] cursor-pointer transition-colors hover:bg-screwfix-blue-shade-2/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
                              onClick={() => onChangeLine(personalisedLine)}
                              type="button"
                            >
                              <span>
                                <Edit className="h-[22px] w-[22px] fill-current lg:h-[26px] lg:w-[26px]" />
                              </span>
                              Change
                            </button>
                            <div className="self-center h-[60%] border-r border-[#ebebeb] sm:hidden"></div>
                            <button
                              aria-label={`Remove personalisation from ${lineName}`}
                              className="flex min-h-[44px] flex-row gap-[8px] items-center justify-center rounded-[5px] bg-transparent px-[30px] py-[16px] text-[16px] font-normal leading-[1.4] text-screwfix-red-shade-1 lg:text-[20px] cursor-pointer transition-colors hover:bg-screwfix-red-shade-1/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
                              onClick={() => onRemoveLine(personalisedLine)}
                              type="button"
                            >
                              <span>
                                <Bin className="h-[22px] w-[22px] fill-current lg:h-[26px] lg:w-[26px]" />
                              </span>
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </ul>
  );
}
